import React from 'react';
import {
  FEATURES,
  FPTI_TAGS,
  FPTI_SPEC,
  LINK_NAME,
} from '../../fpti/fptiConstants';
import { EVENT_NAME } from '../../utility/constants';
import { trackEventWithData } from '../../fpti/analytics';
import { trackLinkClick, trackErrorIM } from '../../fpti/fpti';
import { addFeature } from '../../fpti/helper';

/**
 * Handles initialization of the Venmo payment button.
 * Sets up analytics tracking, adds Venmo feature flag, and enables the button.
 *
 * @param {Object} _data - PayPal data object (unused)
 * @param {Object} actions - PayPal actions object with enable method
 * @param {Function} setIsLoading - Callback to update loading state
 */
export const onInit = (_data, actions, setIsLoading) => {
  addFeature(FEATURES.VENMO);
  trackEventWithData(FPTI_SPEC.LANDING_PAGE_VENMO_BUTTON_SHOWN);
  setIsLoading?.(false);
  actions?.enable?.();
};

/**
 * Handles click events on the Venmo payment button.
 * Tracks the button click event for analytics purposes.
 */
export const onClick = () => {
  trackLinkClick(LINK_NAME.VENMO_BUTTON, {
    eventName: EVENT_NAME.DONATE_LANDING_VENMO_BUTTON_PRESSED,
  });
};

/**
 * Handles cancellation of the Venmo payment flow.
 * Tracks the cancellation event for analytics purposes.
 */
export const onCancel = () => {
  trackEventWithData(FPTI_SPEC.DONATE_VENMO_ORDER_CANCELLED);
};

/**
 * Handles errors that occur during the Venmo payment flow.
 * Tracks error events for analytics and displays error alerts to the user.
 *
 * @param {Object} error - Error object containing status and message
 * @param {Function} handleAlert - Callback function to display alert messages
 */
export const onError = (error, handleAlert) => {
  trackErrorIM({
    errorCode: error?.status || 'donate_venmo_order_failed',
    errorMessage: error?.message || 'Venmo order payment failed.',
    fieldName: 'Venmo Payment',
    additionalData: {
      [FPTI_TAGS.EVENT_NAME]: EVENT_NAME.DONATE_VENMO_ORDER_FAILED,
    },
  });
  handleAlert(true, {
    type: 'error',
    messageId: 'errors.500.error.message',
  });
};

/**
 * Component that handles Venmo payment button ineligibility.
 * Tracks ineligibility events and updates the eligibility state.
 *
 * @param {Object} props - Component props
 * @param {Function} props.setIsEligible - Callback to update eligibility state
 * @returns {null} This component renders nothing (null)
 */
export const InEligibleError = ({ setIsEligible }) => {
  React.useEffect(() => {
    trackEventWithData(FPTI_SPEC.LANDING_PAGE_VENMO_BUTTON_INELIGIBLE);
    setIsEligible(false);
  }, [setIsEligible]);

  return null;
};

/**
 * Handles click events on Venmo alert messages.
 * Tracks alert click events for analytics purposes.
 */
export const onAlertClick = () => {
  trackLinkClick(LINK_NAME.VENMO_ALERT, {
    eventName: EVENT_NAME.DONATE_LANDING_VENMO_ALERT_PRESSED,
  });
};
